import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, Film, ArrowRight } from "lucide-react";

const FEATURED = { src: "/interventi/intervento-3.mp4", poster: "/interventi/intervento-3.jpg" };

const GALLERY = [
  { src: "/interventi/intervento-1.mp4", poster: "/interventi/intervento-1.jpg" },
  { src: "/interventi/intervento-2.mp4", poster: "/interventi/intervento-2.jpg" },
  { src: "/interventi/intervento-4.mp4", poster: "/interventi/intervento-4.jpg" },
];

const MAP_EMBED = "https://www.google.com/maps?q=43.762602,10.462677&z=16&output=embed";
const MAP_LINK = "https://maps.app.goo.gl/xfgFJJGn811s5MkK9";

const VideoFrame = ({ src, poster, testid, className = "" }) => (
  <div className={`group rounded-[28px] bg-gradient-to-b from-[#FBF9F6] to-white p-2.5 sm:p-3 border border-[#E5E1D8] shadow-[0_14px_34px_rgba(0,0,0,0.10)] ${className}`}>
    <div data-testid={testid} className="relative rounded-[20px] overflow-hidden aspect-[9/16] bg-black ring-1 ring-black/10">
      <video
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={poster}
        className="w-full h-full object-cover"
      >
        <source src={src} type="video/mp4" />
        Il tuo browser non supporta la riproduzione video.
      </video>
    </div>
  </div>
);

export const InterventoRecap = ({ showCta = true }) => {
  return (
    <section id="intervento-11-luglio" className="scroll-mt-24 relative py-16 sm:py-24 bg-white" data-testid="intervento-recap">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#2E4F3B]/10 border border-[#2E4F3B]/25 px-4 py-1.5 mb-6">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#2E4F3B]" />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#2E4F3B]">
            11 Luglio 2026 · Fatto insieme
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          {/* Testo + mappa piccola */}
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
              Il nostro primo <span className="italic font-light text-[#2E4F3B]">grande intervento.</span>
            </h2>
            <div className="mt-6 space-y-4 text-[#5C5C5C] leading-relaxed text-sm sm:text-base">
              <p>
                All'alba di <strong className="text-[#1A1A1A]">sabato 11 luglio 2026</strong>, i volontari
                di <strong className="text-[#1A1A1A]">Per Asciano</strong> si sono ritrovati per il primo
                grande intervento dell'associazione: una mattinata di lavoro concreto per aiutare due
                proprietà di Asciano colpite dall'incendio che ha devastato il Monte Faeta.
              </p>
              <p>
                Insieme abbiamo rimosso alberi caduti e materiale vegetale bruciato, ripulito le aree
                danneggiate e dato una mano concreta alle famiglie colpite. È stata una mattinata di
                fatica, sudore e sorrisi, che dimostra cosa significa davvero <strong className="text-[#1A1A1A]">fare comunità</strong>.
              </p>
              <p>
                Un grazie di cuore a tutte le persone che c'erano: questo è solo l'inizio del percorso
                di rinascita di Asciano e del Monte Faeta. 🌳
              </p>
            </div>

            {showCta && (
              <Link
                to="/partecipa"
                data-testid="recap-cta-volontario"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a94c30] text-white font-bold px-7 py-4 transition-all shadow-lg hover:-translate-y-0.5"
              >
                Unisciti alle prossime iniziative
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}

            {/* Mappa piccola */}
            <div className="mt-8 max-w-sm">
              <div className="flex items-center gap-2 mb-2.5">
                <MapPin className="w-4 h-4 text-[#C15C3D]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C]">Dove siamo intervenuti · Asciano (PI)</span>
              </div>
              <div className="rounded-2xl overflow-hidden border-2 border-[#E5E1D8] shadow-sm">
                <iframe
                  title="Punto dell'intervento — Asciano"
                  data-testid="intervento-map"
                  src={MAP_EMBED}
                  className="w-full h-[180px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-bold text-[#2E4F3B] hover:text-[#1f3828] underline underline-offset-4"
              >
                Apri in Google Maps
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Video in evidenza (15 secondi) */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Film className="w-4 h-4 text-[#C15C3D]" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C]">Il momento clou</span>
            </div>
            <VideoFrame
              src={FEATURED.src}
              poster={FEATURED.poster}
              testid="intervento-video-featured"
              className="max-w-md mx-auto lg:mx-0"
            />
          </div>
        </div>

        {/* Galleria video (autoplay) */}
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-5">
            <Film className="w-4 h-4 text-[#C15C3D]" />
            <span className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C]">Altri momenti della giornata</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-3xl">
            {GALLERY.map((v, i) => (
              <VideoFrame key={i} src={v.src} poster={v.poster} testid={`intervento-video-${i}`} />
            ))}
          </div>
          <p className="mt-4 text-xs text-[#9a9a9a] flex items-center gap-1.5">
            <Film className="w-3.5 h-3.5" />
            I video partono in automatico senza audio. Tocca un video per attivare l'audio.
          </p>
        </div>
      </div>
    </section>
  );
};
